import { ResponseResolver, MockedRequest, restContext } from 'msw';
import { delayedResponse } from '@/utils/useMswCustomResponse';
import type { QuestionCategory } from '../api/types';

const getAll: ResponseResolver<MockedRequest, typeof restContext> = (
  req,
  res,
  ctx,
) => {
  return delayedResponse(
    ctx.status(200),
    ctx.json<{ questionCategories: QuestionCategory[] }>({
      questionCategories: [
        {
          id: 1,
          name: 'アプリについて',
          questions: [
            {
              id: 1,
              question: 'このアプリでできることは何ですか？',
              answer:
                '<p>このアプリでは、駐車場の検索・予約・決済が可能です。時間貸し駐車場や月極駐車場の利用に対応しています。</p>',
            },
            {
              id: 2,
              question: 'アプリの対応OSは何ですか？',
              answer:
                '<p>iOS 13.0以降、Android 8.0以降に対応しています。最新のOSでのご利用を推奨しています。</p>',
            },
            {
              id: 3,
              question: '会員登録は必須ですか？',
              answer:
                '<p>駐車場の検索は会員登録なしでも可能ですが、予約や決済を行うには会員登録が必要です。</p>',
            },
            {
              id: 4,
              question: 'アプリの利用料金はかかりますか？',
              answer:
                '<p>アプリのダウンロードおよび基本的な機能の利用は無料です。駐車場の利用料金のみお支払いいただきます。</p>',
            },
            {
              id: 5,
              question: 'パスワードを忘れた場合はどうすればいいですか？',
              answer:
                '<p>ログイン画面の「パスワードをお忘れの方」からパスワードの再設定が可能です。登録されたメールアドレスに再設定用のリンクをお送りします。</p>',
            },
            {
              id: 6,
              question: 'アプリの通知を設定できますか？',
              answer:
                '<p>はい、設定画面からプッシュ通知のオン/オフを切り替えることができます。予約のリマインダーやキャンペーン情報などの通知を受け取れます。</p>',
            },
            {
              id: 7,
              question: '複数の車両を登録できますか？',
              answer:
                '<p>はい、マイページから複数の車両情報を登録することが可能です。利用時に使用する車両を選択できます。</p>',
            },
            {
              id: 8,
              question: 'アプリが起動しない場合はどうすればいいですか？',
              answer:
                '<p>アプリを一度削除して再インストールしてください。それでも改善しない場合は、お問い合わせフォームからご連絡ください。</p>',
            },
            {
              id: 9,
              question: '退会したい場合はどうすればいいですか？',
              answer:
                '<p>マイページの設定画面から退会手続きが可能です。退会されると、ご利用履歴やポイントなどのデータはすべて削除されますのでご注意ください。</p>',
            },
            {
              id: 10,
              question: 'アプリのバージョン確認方法を教えてください',
              answer:
                '<p>マイページの設定画面からアプリのバージョン情報を確認できます。最新バージョンへのアップデート案内も表示されます。</p>',
            },
          ],
        },
        {
          id: 2,
          name: '駐車場の利用',
          questions: [
            {
              id: 1,
              question: '駐車場の検索方法を教えてください',
              answer:
                '<p>ホーム画面の検索欄から、住所や施設名、現在地から駐車場を検索できます。地図からも選択可能です。</p>',
            },
            {
              id: 2,
              question: '予約のキャンセルはできますか？',
              answer:
                '<p>はい、利用開始時刻の30分前までキャンセルが可能です。マイページの予約履歴から手続きできます。</p>',
            },
            {
              id: 3,
              question: '予約時間を延長することはできますか？',
              answer:
                '<p>はい、利用中の駐車場ページから延長手続きが可能です。空き状況により延長できない場合があります。</p>',
            },
            {
              id: 4,
              question: '当日予約はできますか？',
              answer:
                '<p>はい、空きがあれば当日予約も可能です。即時利用開始も対応しています。</p>',
            },
            {
              id: 5,
              question: '予約の確認メールは届きますか？',
              answer:
                '<p>予約完了後、登録されたメールアドレスに確認メールをお送りします。マイページからも確認できます。</p>',
            },
            {
              id: 6,
              question: '駐車場の営業時間は確認できますか？',
              answer:
                '<p>各駐車場の詳細ページに営業時間や利用可能時間が記載されています。24時間営業の駐車場も多数あります。</p>',
            },
            {
              id: 7,
              question: '車のサイズ制限はありますか？',
              answer:
                '<p>駐車場によって異なります。各駐車場の詳細ページで車高・車幅・車長の制限を確認できます。</p>',
            },
            {
              id: 8,
              question: '駐車場の設備情報は確認できますか？',
              answer:
                '<p>はい、屋根付き、防犯カメラ、照明、バリアフリー対応などの設備情報を確認できます。</p>',
            },
            {
              id: 9,
              question: 'お気に入り登録はできますか？',
              answer:
                '<p>はい、駐車場詳細ページからお気に入り登録ができます。マイページから一覧表示できます。</p>',
            },
            {
              id: 10,
              question: '利用履歴はどこで確認できますか？',
              answer:
                '<p>マイページの利用履歴から、過去の利用記録を確認できます。領収書の発行も可能です。</p>',
            },
          ],
        },
        {
          id: 3,
          name: '決済・料金',
          questions: [
            {
              id: 1,
              question: '利用可能な決済方法を教えてください',
              answer:
                '<p>クレジットカード、デビットカード、電子マネー、キャリア決済に対応しています。</p>',
            },
            {
              id: 2,
              question: '領収書は発行できますか？',
              answer:
                '<p>はい、ご利用履歴から領収書を発行することができます。<a style="color: orange;" href="https://google.com">詳しくはこちら</a></p>',
            },
            {
              id: 3,
              question: 'クレジットカードは何種類使えますか？',
              answer:
                '<p>VISA、MasterCard、JCB、American Express、Diners Clubがご利用いただけます。</p>',
            },
            {
              id: 4,
              question: '料金の計算方法を教えてください',
              answer:
                '<p>時間貸しの場合、利用時間に応じて自動計算されます。最大料金設定がある駐車場もあります。</p>',
            },
            {
              id: 5,
              question: 'キャンセル料はかかりますか？',
              answer:
                '<p>利用開始30分前までのキャンセルは無料です。それ以降は駐車場により異なります。</p>',
            },
            {
              id: 6,
              question: 'ポイントは貯まりますか？',
              answer:
                '<p>はい、夢なびポイントが利用額に応じて貯まります。貯まったポイントは次回のお支払いに使用できます。</p>',
            },
            {
              id: 7,
              question: 'クーポンは使えますか？',
              answer:
                '<p>はい、お支払い時にクーポンコードを入力することで割引が適用されます。</p>',
            },
            {
              id: 8,
              question: '法人での利用は可能ですか？',
              answer:
                '<p>はい、法人アカウントでの登録が可能です。請求書払いにも対応しています。</p>',
            },
            {
              id: 9,
              question: '消費税は含まれていますか？',
              answer:
                '<p>表示されている料金には消費税が含まれています。</p>',
            },
            {
              id: 10,
              question: '決済エラーが発生した場合はどうすればいいですか？',
              answer:
                '<p>カード情報を再確認の上、再度お試しください。解決しない場合はサポートセンターにお問い合わせください。</p>',
            },
          ],
        },
        {
          id: 4,
          name: '月極駐車場',
          questions: [
            {
              id: 1,
              question: '月極駐車場の申し込み方法を教えてください',
              answer:
                '<p>アプリから希望の駐車場を選択し、仮申込を行います。審査後、本申込へ進んでいただきます。</p>',
            },
            {
              id: 2,
              question: '仮申込とは何ですか？',
              answer:
                '<p>月極駐車場の正式契約前の簡易的な申込手続きです。仮申込後に審査が行われます。</p>',
            },
            {
              id: 3,
              question: '本申込に必要な書類を教えてください',
              answer:
                '<p>本人確認書類、車検証のコピー、印鑑証明書（法人の場合）が必要です。詳細は審査後にご案内します。</p>',
            },
            {
              id: 4,
              question: '契約期間はどのくらいですか？',
              answer:
                '<p>基本的に1ヶ月単位での契約となります。自動更新も可能です。</p>',
            },
            {
              id: 5,
              question: '初期費用はいくらかかりますか？',
              answer:
                '<p>契約手数料、保証金、日割り料金などが初期費用として必要です。金額は駐車場により異なります。</p>',
            },
            {
              id: 6,
              question: '月極駐車場の解約方法を教えてください',
              answer:
                '<p>マイページの契約管理から解約手続きが可能です。1ヶ月前までに申請が必要です。</p>',
            },
            {
              id: 7,
              question: '区画（車室）の変更はできますか？',
              answer:
                '<p>空きがあれば変更可能です。マイページからお問い合わせいただくか、サポートセンターにご連絡ください。</p>',
            },
            {
              id: 8,
              question: '月額料金の支払い方法を教えてください',
              answer:
                '<p>クレジットカードによる自動引き落としとなります。毎月の支払日は契約開始日に基づきます。</p>',
            },
            {
              id: 9,
              question: '複数台契約することはできますか？',
              answer:
                '<p>はい、空きがあれば複数区画の契約が可能です。それぞれ別途申込が必要です。</p>',
            },
            {
              id: 10,
              question: '契約内容の確認はどこでできますか？',
              answer:
                '<p>マイページの契約管理から、現在の契約内容や支払い状況を確認できます。</p>',
            },
          ],
        },
        {
          id: 5,
          name: 'トラブル・サポート',
          questions: [
            {
              id: 1,
              question: '駐車場に入れない場合はどうすればいいですか？',
              answer:
                '<p>予約完了メールに記載されている開錠コードをご確認ください。それでも入れない場合は緊急連絡先にお電話ください。</p>',
            },
            {
              id: 2,
              question: '車に傷がついた場合はどうすればいいですか？',
              answer:
                '<p>すぐにサポートセンターにご連絡ください。駐車場の管理会社と連携して対応いたします。</p>',
            },
            {
              id: 3,
              question: '駐車場内での事故はどうなりますか？',
              answer:
                '<p>警察とサポートセンターにご連絡ください。防犯カメラの映像確認などサポートいたします。</p>',
            },
            {
              id: 4,
              question: '予約したのに駐車できない場合はどうすればいいですか？',
              answer:
                '<p>すぐにサポートセンターにご連絡ください。代替駐車場のご案内や返金対応をいたします。</p>',
            },
            {
              id: 5,
              question: 'サポートセンターの営業時間を教えてください',
              answer:
                '<p>平日9:00〜18:00、土日祝日10:00〜17:00で対応しています。緊急時は24時間対応の連絡先もあります。</p>',
            },
            {
              id: 6,
              question: 'お問い合わせ方法を教えてください',
              answer:
                '<p>アプリ内のお問い合わせフォーム、電話、メールで受け付けています。マイページからもアクセスできます。</p>',
            },
            {
              id: 7,
              question: '個人情報の取り扱いについて教えてください',
              answer:
                '<p>個人情報は厳重に管理し、サービス提供以外の目的では使用いたしません。詳細はプライバシーポリシーをご確認ください。</p>',
            },
            {
              id: 8,
              question: 'アカウントの乗っ取りが疑われる場合はどうすればいいですか？',
              answer:
                '<p>すぐにパスワードを変更し、サポートセンターにご連絡ください。不正利用があった場合は速やかに対応いたします。</p>',
            },
            {
              id: 9,
              question: 'アプリの不具合を報告したい',
              answer:
                '<p>お問い合わせフォームから不具合の詳細をご報告ください。スクリーンショットを添付していただけると助かります。</p>',
            },
            {
              id: 10,
              question: 'サービス改善の要望はどこに送ればいいですか？',
              answer:
                '<p>お問い合わせフォームからご意見・ご要望をお送りください。今後のサービス改善の参考にさせていただきます。</p>',
            },
          ],
        },
      ],
    }),
  );
};

const mockFaq = { getAll };
export default mockFaq;
